import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { ColorPicker, Tooltip } from "../utils/local-antd";
import { Rounded } from "../theme/Rounded";
import { Spacing } from "../theme/Spacing";
import { AtiraIcon } from "./AtiraIcon";
import { Clickable } from "./Clickable";
import { Flex } from "./Flex";
import { InputProps } from "./Inputs/InputProps";
import { Text } from "./Text";

const StyledWrapper = styled(Flex)`
  gap: ${Spacing.s};
  flex-direction: column;
`;

const Title = styled(Text)`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
`;

const InfoSpan = styled.span`
  display: inline-flex;
`;

const InputWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: flex-start;
  width: fit-content;
  gap: ${Spacing.s};
  align-items: flex-start;
`;

const PickerRow = styled(Flex)`
  justify-content: flex-start;
  gap: ${Spacing.s};
  align-items: center;
`;

const TriggerButton = styled.button<{ $disabled?: boolean }>`
  min-height: 2.3rem;
  min-width: 8rem;
  border-radius: ${Rounded.md};
  padding: 0.25rem 0.5rem;
  border: 1px solid ${({ theme }) => theme.lightgray};
  background-color: ${({ theme }) => theme.white};
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.s};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.65 : 1)};
`;

const TriggerSwatch = styled.span<{ $color?: string | null }>`
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  border-radius: ${Rounded.sm};
  border: 1px solid ${({ theme }) => theme.lightgray};
  border-style: ${({ $color }) => ($color ? "none" : "dashed")};
  position: relative;
  background: ${({ $color }) =>
    $color
      ? `repeating-conic-gradient(#d9d9d9 0% 25%, ${$color} 0% 50%) 50% / 8px 8px`
      : "transparent"};

  ${({ $color }) =>
    !$color
      ? `
    &::after {
      content: "";
      position: absolute;
      inset: 0.15rem;
      background: linear-gradient(
        135deg,
        transparent calc(50% - 0.5px),
        rgba(0, 0, 0, 0.35) calc(50% - 0.5px),
        rgba(0, 0, 0, 0.35) calc(50% + 0.5px),
        transparent calc(50% + 0.5px)
      );
    }
  `
      : ""}
`;

const TriggerText = styled.span`
  min-width: 0;
  font-size: 0.875rem;
  line-height: 1;
  color: ${({ theme }) => theme.black};
`;

const StyledColorPicker = styled(ColorPicker)`
  &.ant-color-picker-trigger {
    min-height: 2.3rem;
    border-radius: ${Rounded.md};
    padding: 0.25rem 0.5rem;
  }

  .ant-color-picker-trigger-text {
    font-size: 0.875rem;
  }
`;

type AtiraColorPickerProps = Omit<InputProps, "value"> & {
  value?: string | null;
};

export const AtiraColorPicker: React.FC<AtiraColorPickerProps> = ({
  title,
  valid = true,
  errorMessage,
  info,
  value,
  className,
  dataTestId,
  disabled,
  onChange,
}) => {
  const { t } = useTranslation();

  const emitChange = (nextValue: string | null) => {
    onChange?.({ target: { value: nextValue } } as any);
  };

  return (
    <StyledWrapper>
      {title ? (
        <Title>
          {info ? (
            <Tooltip title={info}>
              <InfoSpan>
                <AtiraIcon icon={faInfoCircle} color="main" />
              </InfoSpan>
            </Tooltip>
          ) : null}
          <span>{title}</span>
        </Title>
      ) : null}

      <InputWrapper>
        <PickerRow>
          <StyledColorPicker
            disabled={disabled}
            disabledFormat
            format="hex"
            onChange={(color) => emitChange(color.toHexString().toUpperCase())}
            value={value ?? null}
          >
            <TriggerButton
              type="button"
              className={className}
              data-testid={dataTestId}
              disabled={disabled}
              $disabled={disabled}
            >
              <TriggerSwatch $color={value} />
              <TriggerText>{value ? value.toUpperCase() : ""}</TriggerText>
            </TriggerButton>
          </StyledColorPicker>

          {value ? (
            <Clickable onClick={() => emitChange(null)}>
              <Tooltip title={t("common.reset")}>
                <span />
                <AtiraIcon icon={faTrash} color="main" />
              </Tooltip>
            </Clickable>
          ) : null}
        </PickerRow>
      </InputWrapper>

      {errorMessage && !valid ? (
        <Text fontSize="s" color="danger">
          {errorMessage}
        </Text>
      ) : null}
    </StyledWrapper>
  );
};
